import {Graphics} from 'pixi.js';
import {string2hex} from "./utils";
import {WaveformData} from "waveform-data";
import {getSamplesPerSecond, Scale} from "./coordinates";


type DrawWaveFormOpts = {
  width: number,
  height: number,
  data: WaveformData,
  frameOffset: number,
  mono?: boolean,
  scale: Scale,
  displayGain?: number,
};


export function getRecommendedWaveformDisplayGain(data: WaveformData): number {
  const peaks: number[] = [];
  for (let sampleIdx = 0; sampleIdx < data.adapter.length; sampleIdx++) {
    const peak = getPeakAmplitude(data, sampleIdx);
    if (peak > 0) {
      peaks.push(peak);
    }
  }

  if (!peaks.length) {
    return 1;
  }

  peaks.sort((a, b) => a - b);
  const percentileIndex = Math.min(peaks.length - 1, Math.floor((peaks.length - 1) * 0.95));
  const percentile95Peak = peaks[percentileIndex];
  if (!percentile95Peak) {
    return 1;
  }

  const targetPeak = 104;
  return clamp(targetPeak / percentile95Peak, 0.75, 8);
}


export function drawWaveform(g: Graphics, opts: DrawWaveFormOpts) {
  const { width, height, data, frameOffset, mono, scale, displayGain } = opts;

  _drawWaveform(
    g,
    data,
    frameOffset,
    width,
    height,
    mono || false,
    scale,
    displayGain || 1
  );
}


function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}


function getPeakAmplitude(waveformData: WaveformData, sampleIdx: number) {
  return Math.max(
    Math.abs(waveformData.min_sample(sampleIdx)),
    Math.abs(waveformData.max_sample(sampleIdx))
  );
}


function clampAmplitude(amplitude: number, displayGain: number) {
  return clamp(amplitude * displayGain, -128, 128);
}


function getSampleIndexAtPixel(
  x: number,
  firstSampleIdx: number,
  samplesPerPixel: number,
  waveformData: WaveformData,
) {
  let sampleIdx = firstSampleIdx + (x * samplesPerPixel);
  if (samplesPerPixel > 1) {
    sampleIdx = Math.round(sampleIdx / samplesPerPixel) * samplesPerPixel;
  }
  sampleIdx = Math.round(sampleIdx);
  if (sampleIdx >= waveformData.adapter.length - 1) {
    return null;
  }
  return sampleIdx;
}


/**
 * waveform-data gives us a datastructure. I am not a fan of it, but let's
 * work with it. It is basically just a list of high-low, or min/max pairs.
 */
function _drawWaveform(
  g: Graphics,
  waveformData: WaveformData,
  firstSampleIdx: number,
  width: number,
  height: number,
  mono: boolean,
  scale: Scale,
  displayGain: number,
) {
  const samplesPerSecond = getSamplesPerSecond(waveformData);
  const pixelsPerSecond = scale.pixelsPerSecond;
  const samplesPerPixel = samplesPerSecond / pixelsPerSecond;

  const color = string2hex('#c0c0c0');
  g.beginFill(color, 0.9);
  g.lineStyle(1, color, 0.9)

  if (mono) {
    const centerY = height / 2;
    const halfHeight = Math.max(1, (height / 2) - 2);
    const amplitudes: number[] = [];

    for (let x = 0; x < width; x++) {
      const sampleIdx = getSampleIndexAtPixel(x, firstSampleIdx, samplesPerPixel, waveformData);
      if (sampleIdx === null) {
        break;
      }

      const peakAmplitude = clampAmplitude(getPeakAmplitude(waveformData, sampleIdx), displayGain);
      amplitudes.push(peakAmplitude);
    }

    if (!amplitudes.length) {
      g.endFill();
      return;
    }

    g.moveTo(0, centerY);

    for (let x = 0; x < amplitudes.length; x++) {
      const y = centerY + (amplitudes[x] / 128) * halfHeight;
      g.lineTo(x, y);
    }

    for (let x = amplitudes.length - 1; x >= 0; x--) {
      const y = centerY - (amplitudes[x] / 128) * halfHeight;
      g.lineTo(x, y);
    }

    g.lineTo(0, centerY);
    g.endFill();
    return;
  }

  const scaleY = makeScaleY(256, 128);

  g.moveTo(0, scaleY(0, height))

  for (let x = 0; x < width; x++) {
    const sampleIdx = getSampleIndexAtPixel(x, firstSampleIdx, samplesPerPixel, waveformData);
    if (sampleIdx === null) {
      break;
    }

    const val = clampAmplitude(waveformData.min_sample(sampleIdx), displayGain);
    g.lineTo(x + 0.5, scaleY(val, height) + 0.5);
  }

  for (let x = width - 1; x >= 0; x--) {
    const sampleIdx = getSampleIndexAtPixel(x, firstSampleIdx, samplesPerPixel, waveformData);
    if (sampleIdx === null) {
      continue;
    }

    const val = clampAmplitude(waveformData.max_sample(sampleIdx), displayGain);
    g.lineTo(x + 0.5, scaleY(val, height) + 0.5);
  }

  g.endFill();
}


/**
 * For a given amplitude, where is the Y position?
 *
 * With range 256, and offset 128, this would do:
 *
 * At 128, returns 0 (the top side of the waveform)
 * At -128, returns the full height (the bottom side waveform)
 * At 0, returns half the height.
 */
function makeScaleY(range: number, offset: number) {
  function scaleY(amplitude: number, height: number) {
    return height - ((amplitude + offset) * height) / range;
  }

  return scaleY;
}
